// src/components/lekhi/TextView.js
import React, { useEffect, useRef, useState } from 'react';
import './TextView.css';
import {
  normalizeLekhiText,
  renderLekhiInline,
  LekhiWatermark,
  lekhiProtectProps,
  LEKHI_PROTECT_CLASS
} from './LekhiProtect';

// ---------- Block parser (simple markdown subset, indentation flattened) ----------
const isTableSep = (line) => /^\|?[\s:|-]+\|?$/.test(line) && line.includes('-');

const splitRow = (line) => {
  let t = line.trim();
  if (t.startsWith('|')) t = t.slice(1);
  if (t.endsWith('|')) t = t.slice(0, -1);
  return t.split('|').map(c => c.trim());
};

const parseBlocks = (raw) => {
  if (!raw) return [];
  const lines = normalizeLekhiText(raw).split('\n').map(l => l.trim());
  const blocks = [];
  let para = [];
  let i = 0;
  const flush = () => {
    if (para.length) { blocks.push({ type: 'para', lines: para }); para = []; }
  };

  while (i < lines.length) {
    const line = lines[i];
    if (!line) { flush(); i++; continue; }

    if (/^#{1,6}\s+/.test(line)) {
      flush();
      blocks.push({ type: 'heading', text: line.replace(/^#{1,6}\s+/, '') });
      i++; continue;
    }

    if (line.startsWith('|')) {
      flush();
      const rows = [];
      while (i < lines.length && lines[i].startsWith('|')) {
        if (!isTableSep(lines[i])) rows.push(splitRow(lines[i]));
        i++;
      }
      if (rows.length) blocks.push({ type: 'table', header: rows[0], rows: rows.slice(1) });
      continue;
    }

    const bullet = line.match(/^[-*•]\s+(.*)$/);
    if (bullet) { flush(); blocks.push({ type: 'bullet', text: bullet[1] }); i++; continue; }

    const step = line.match(/^([0-9०-९]+)[.)]\s+(.*)$/);
    if (step) { flush(); blocks.push({ type: 'step', num: step[1] + '.', text: step[2] }); i++; continue; }

    para.push(line);
    i++;
  }
  flush();
  return blocks;
};

const renderBlocks = (blocks) => blocks.map((b, idx) => {
  switch (b.type) {
    case 'heading':
      return <h2 key={idx} className="lk-text-heading">{renderLekhiInline(b.text, `h${idx}`)}</h2>;
    case 'bullet':
      return (
        <div key={idx} className="lk-text-item">
          <span className="lk-text-marker">•</span>
          <span className="lk-text-item-body">{renderLekhiInline(b.text, `b${idx}`)}</span>
        </div>
      );
    case 'step':
      return (
        <div key={idx} className="lk-text-item">
          <span className="lk-text-marker lk-text-step-num">{b.num}</span>
          <span className="lk-text-item-body">{renderLekhiInline(b.text, `s${idx}`)}</span>
        </div>
      );
    case 'table':
      return (
        <div key={idx} className="lk-table-wrap">
          <table className="lk-table">
            <thead>
              <tr>{b.header.map((c, ci) => <th key={ci}>{renderLekhiInline(c, `th${idx}-${ci}`)}</th>)}</tr>
            </thead>
            <tbody>
              {b.rows.map((r, ri) => (
                <tr key={ri}>{r.map((c, ci) => <td key={ci}>{renderLekhiInline(c, `td${idx}-${ri}-${ci}`)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return (
        <p key={idx} className="lk-text-paragraph">
          {b.lines.map((l, li) => (
            <React.Fragment key={li}>
              {li > 0 && <br />}
              {renderLekhiInline(l, `p${idx}-${li}`)}
            </React.Fragment>
          ))}
        </p>
      );
  }
});

// ---------- Component ----------
// reference = true → Library: पुढे always visible (no read-to-the-end lock)
const TextView = ({ textContent, language, onComplete, onBack, reference = false }) => {
  const labels = language === 'mr'
    ? { back: '← मागे', next: 'पुढे', title: 'वाचा', hint: "शेवटपर्यंत वाचा, मग 'पुढे' बटण दिसेल" }
    : { back: '← Back', next: 'Continue', title: 'Read', hint: 'Read to the end to continue' };

  const text = textContent?.text || '';
  const [reachedEnd, setReachedEnd] = useState(false);
  const endRef = useRef(null);

  // Continue appears only once the end of the text has been seen
  useEffect(() => {
    setReachedEnd(false);
    const el = endRef.current;
    if (!el) return undefined;
    if (!('IntersectionObserver' in window)) { setReachedEnd(true); return undefined; }
    const obs = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) {
        setReachedEnd(true);
        obs.disconnect();
      }
    }, { threshold: 0 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [text]);

  return (
    <div
      className={`lk-text-container ${LEKHI_PROTECT_CLASS}`}
      {...lekhiProtectProps}
    >
      <LekhiWatermark />

      <div className="lk-text-header">
        <button className="back-btn" onClick={onBack}>{labels.back}</button>
        <div className="lk-text-title">{labels.title}</div>
        <div style={{ width: 60 }} />
      </div>

      <div className="lk-text-body">
        <div className="lk-text-content">
          {renderBlocks(parseBlocks(text))}
          <div ref={endRef} className="lk-text-end" />
        </div>
      </div>

      <div className="lk-text-footer">
        {(reachedEnd || reference) ? (
          <button className="lk-text-next-btn" onClick={onComplete}>{labels.next}</button>
        ) : (
          <div className="lk-text-hint"><span className="lk-text-hint-arrow">⬇</span>{labels.hint}</div>
        )}
      </div>
    </div>
  );
};

export default TextView;